import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import styles from './fix-pager.module.scss';
import { FixPagerArrow } from '../fix-pager-arrow';
import { FixPagerNumber } from '../fix-pager-number';

export interface FixPagerProps {
    current: number;
    total: number;
    currentUrl?: string;
    onChange?: (page: number) => void;
    className?: string;
}

export const FixPager: React.FC<FixPagerProps> = ({ current, total, onChange, className, currentUrl }) => {
    const safeTotal = Math.max(0, Math.floor(Number.isFinite(total) ? total : 0));
    const safeCurrent = safeTotal > 0
        ? clamp(Math.floor(Number.isFinite(current) ? current : 1), 1, safeTotal)
        : 1;

    const visible = 5;

    const pages = useMemo(
        () => calcPager(safeCurrent, safeTotal, visible),
        [safeCurrent, safeTotal, visible]
    );

    const prevDisabled = safeCurrent <= 1 || safeTotal === 0;
    const nextDisabled = safeCurrent >= safeTotal || safeTotal === 0;

    const lastNavRef = useRef<{ page: number; ts: number } | null>(null);

    const navigateToPage = useCallback((page: number) => {
        if (!Number.isFinite(page)) return;

        const clamped = clamp(Math.floor(page), 1, Math.max(safeTotal, 1));

        const now = Date.now();
        const last = lastNavRef.current;
        if (last && last.page === clamped && now - last.ts < 700) return;

        lastNavRef.current = { page: clamped, ts: now };
        onChange?.(clamped);
    }, [onChange, safeTotal]);

    const handlePrev = useCallback(() => {
        if (!prevDisabled) navigateToPage(safeCurrent - 1);
    }, [prevDisabled, safeCurrent, navigateToPage]);

    const handleNext = useCallback(() => {
        if (!nextDisabled) navigateToPage(safeCurrent + 1);
    }, [nextDisabled, safeCurrent, navigateToPage]);

    const handlePage = useCallback((page: number) => {
        navigateToPage(page);
    }, [navigateToPage]);

    return (
        <div className={classNames(styles.container, className)}>
            <FixPagerArrow
                className={styles.step}
                type="prev"
                disabled={prevDisabled}
                onClick={handlePrev}
                page={safeCurrent - 1}
                currentUrl={currentUrl}
            />

            <div className={styles.numbers}>
                {pages.map((page) => (
                    <FixPagerNumber
                        key={page}
                        page={page}
                        className={classNames(styles.number, { [styles.active]: page === safeCurrent })}
                        active={page === safeCurrent}
                        onClick={() => handlePage(page)}
                        currentUrl={currentUrl}
                    >
                        {page}
                    </FixPagerNumber>
                ))}
            </div>

            <FixPagerArrow
                className={styles.step}
                type="next"
                disabled={nextDisabled}
                onClick={handleNext}
                page={safeCurrent + 1}
                currentUrl={currentUrl}
            />
        </div>
    );
};


function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}

function calcPager(current: number, total: number, size: number) {
    const result: number[] = [];
    if (total <= 0 || size <= 0) return result;

    const windowSize = Math.max(1, Math.floor(size));
    const half = Math.floor((windowSize - 1) / 2);

    let from = current - half;
    let to = current + (windowSize - 1 - half);

    if (from < 1) {
        to += 1 - from;
        from = 1;
    }
    if (to > total) {
        from -= to - total;
        to = total;
    }

    from = Math.max(1, from);
    to = Math.min(total, to);

    for (let i = from; i <= to; i++) result.push(i);
    return result;
}